import React, { createContext, useContext, useState, useEffect } from 'react';
import { createCheckout } from '../lib/shopify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Shopify variant ID mapping based on product slug and size
const SHOPIFY_VARIANT_MAP = {
  'vesta-apex-spray-suit': {
    'XS': 'gid://shopify/ProductVariant/51826025234663',
    'S': 'gid://shopify/ProductVariant/51826025267431',
    'M': 'gid://shopify/ProductVariant/51826025300199',
    'L': 'gid://shopify/ProductVariant/51826025332967',
    'XL': 'gid://shopify/ProductVariant/51826025365735',
    '2XL': 'gid://shopify/ProductVariant/51826025398503',
  },
  'vesta-apex-spray-suit-rotation-pack': {
    'XS': 'gid://shopify/ProductVariant/51826111807719',
    'S': 'gid://shopify/ProductVariant/51826111840487',
    'M': 'gid://shopify/ProductVariant/51826111873255',
    'L': 'gid://shopify/ProductVariant/51826111906023',
    'XL': 'gid://shopify/ProductVariant/51826111938791',
    '2XL': 'gid://shopify/ProductVariant/51826111971559',
  },
  'vesta-wash-bag': {
    'One Size': 'gid://shopify/ProductVariant/51826165809383',
    'Default Title': 'gid://shopify/ProductVariant/51826165809383',
  },
  '2-1-1-1-aluminium-paint-mixing-stick-pk-2': {
    'One Size': 'gid://shopify/ProductVariant/52252538863847',
    'Default Title': 'gid://shopify/ProductVariant/52252538863847',
  },
};

// Resolve the Shopify checkout variant ID for a product + selected size.
// Prefers the product's LIVE Shopify variants (works for any new product),
// falling back to the curated map for the brief pre-load window / legacy items.
const resolveVariantId = (product, selectedSize) => {
  const variants = product?.variants;
  if (variants && variants.length) {
    if (variants.length === 1) return variants[0].id;
    const match = variants.find(
      (v) =>
        v.title === selectedSize ||
        (v.options || []).some((o) => o.value === selectedSize)
    );
    return (match || variants[0]).id;
  }
  return (
    SHOPIFY_VARIANT_MAP[product?.slug]?.[selectedSize] ||
    SHOPIFY_VARIANT_MAP[product?.slug]?.['One Size'] ||
    null
  );
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('vesta-cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  useEffect(() => {
    localStorage.setItem('vesta-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, selectedSize = 'One Size', selectedColor = null, quantity = 1) => {
    const variantId = resolveVariantId(product, selectedSize);
    setCartItems(prevItems => {
      const existingIndex = prevItems.findIndex(
        item => 
          item.id === product.id && 
          item.selectedSize === selectedSize &&
          item.selectedColor?.name === selectedColor?.name
      );

      if (existingIndex >= 0) {
        const updatedItems = [...prevItems];
        updatedItems[existingIndex].quantity += quantity;
        return updatedItems;
      }

      return [...prevItems, {
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        currency: product.currency,
        image: product.images[0],
        selectedSize,
        selectedColor,
        quantity,
        variantId
      }];
    });
  };

  const removeFromCart = (itemId, selectedSize, selectedColorName) => {
    setCartItems(prevItems => 
      prevItems.filter(item => 
        !(item.id === itemId && 
          item.selectedSize === selectedSize && 
          item.selectedColor?.name === selectedColorName)
      )
    );
  };

  const updateQuantity = (itemId, selectedSize, selectedColorName, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId, selectedSize, selectedColorName);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && 
        item.selectedSize === selectedSize && 
        item.selectedColor?.name === selectedColorName
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Convert cart items to Shopify line items and create checkout
  const proceedToCheckout = async () => {
    if (cartItems.length === 0) return null;
    
    setIsCheckingOut(true);
    
    try {
      // Map cart items to Shopify variant IDs (captured live at add-time;
      // fall back to the curated map for any legacy cart items).
      const lineItems = cartItems.map(item => {
        const variantId = item.variantId
          || SHOPIFY_VARIANT_MAP[item.slug]?.[item.selectedSize]
          || SHOPIFY_VARIANT_MAP[item.slug]?.['One Size'];

        if (!variantId) {
          throw new Error(`Unable to find product variant for ${item.name}`);
        }
        
        return {
          variantId,
          quantity: item.quantity,
        };
      });

      // Create Shopify checkout
      const checkout = await createCheckout(lineItems);
      
      // Clear local cart after successful checkout creation
      clearCart();
      
      // Redirect to Shopify checkout
      window.location.href = checkout.webUrl;
      
      return checkout;
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      proceedToCheckout,
      isCheckingOut
    }}>
      {children}
    </CartContext.Provider>
  );
};
